<template>
	<p-message v-if="!query.account_manager_id" :closable="false">Select an account manager to begin...</p-message>
	<query-form v-model:query="query" :loading="loading" @apply="getReportData" @abort="clearQuery">
		<template #display>
			<div class="display-value">
				<div class="label">Date Range:</div>
				<div class="value">{{ formatDate(query.date_range, 'ddd, MMM D, YYYY') }}</div>
			</div>
			<div class="display-value">
				<div class="label">Account Manager:</div>
				<div class="value">{{ displayLabel(query.account_manager_id, account_manager_options) }}</div>
			</div>
			<div class="display-value">
				<div class="label">Campaign Type:</div>
				<div class="value">{{ displaySelected(query.filters.campaign_type, campaign_type_options) }}</div>
			</div>
			<div class="display-value">
				<div class="label">Vertical:</div>
				<div class="value">{{ displaySelected(query.filters.vertical_id, $root.appStore.verticalOptions) }}</div>
			</div>
			<div class="display-value">
				<div class="label">Grouping:</div>
				<div class="value">{{ displayLabel(query.grouping, grouping_options) }}</div>
			</div>
		</template>
		<template #utils>
			<p-button
				v-tooltip.top="'Refresh'"
				icon="pi pi-refresh"
				aria-label="Refresh"
				class="mr-2"
				@click="getReportData(query, true)"
			/>
		</template>
		<template #form="form">
			<row>
				<column max-width="300px">
					<div class="control-group">
						<div class="inner">
							<label class="control-label">Date Range:</label>
							<div class="controls">
								<div class="field">
									<date-range-picker v-model="form.query.date_range" :date-options="date_picker_options" />
								</div>
							</div>
						</div>
					</div>
					<div class="control-group">
						<div class="inner">
							<label class="control-label">Vertical:</label>
							<div class="controls">
								<div class="field">
									<p-multi-select
										v-model="form.query.filters.vertical_id"
										:options="$root.appStore.verticalOptions"
										option-label="label"
										option-value="value"
										placeholder="Filter by Vertical"
									/>
								</div>
							</div>
						</div>
					</div>
				</column>
				<gutter size="40px" />
				<column max-width="300px">
					<div class="control-group">
						<div class="inner">
							<label class="control-label">Account Manager:</label>
							<div class="controls">
								<div class="field">
									<p-dropdown
										v-model="form.query.account_manager_id"
										:options="account_manager_options"
										option-label="label"
										option-value="value"
										placeholder="Select Account Manager"
									/>
								</div>
							</div>
						</div>
					</div>
					<div class="control-group">
						<div class="inner">
							<label class="control-label">Campaign Type:</label>
							<div class="controls">
								<div class="field">
									<p-multi-select
										v-model="form.query.filters.campaign_type"
										:options="campaign_type_options"
										option-label="label"
										option-value="value"
										placeholder="Filter by Campaign Type"
									/>
								</div>
							</div>
						</div>
					</div>
				</column>
				<gutter size="40px" />
				<column max-width="300px">
					<div class="control-group">
						<div class="inner">
							<label class="control-label">Grouping:</label>
							<div class="controls">
								<div class="field">
									<radio-group v-model="form.query.grouping" :options="grouping_options" stacked />
								</div>
							</div>
						</div>
					</div>
				</column>
				<gutter size="40px" />
				<column></column>
			</row>
		</template>
	</query-form>
	<div class="quick-filters flex justify-contents-start">
		<h2 class="am-name">{{ displayLabel(query.account_manager_id, account_manager_options) }}</h2>
		<quick-filter v-model="metric_view" value="spend">Spend (CPL)</quick-filter>
		<quick-filter v-model="metric_view" value="cpa">CPA (CR)</quick-filter>
	</div>
	<gutter size="10px" />
	<div class="card padded table-card">
		<div class="data-table-wrapper">
			<p-data-table
				:value="report_data"
				ref="am_book_tracker_table"
				data-key="account_id"
				class="am-book-tracker-table"
				sort-field="account_name"
				:sort-order="1"
				scrollable
				scrollHeight="calc(100vh - 420px)"
				:frozen-value="totalsRows"
			>
				<template #empty>
					<div class="dim">No Data</div>
				</template>
				<template #loading>
					<line-loader :show="loading" />
				</template>
				<p-column header="Account" sortable field="account_name" frozen>
					<template #body="row">
						<template v-if="row.data.total">
							<strong>{{ row.data.account_name }}</strong>
						</template>
						<template v-else>
							<router-link
								:to="`/marketplaces/manage/${row.data.account_id.substring(0, 3)}/accounts/manage/${row.data.account_id}/reports/account-activity`"
								>{{ row.data.account_name?.trim() || 'Unknown' }}</router-link
							><br />
							<div v-if="row.data.account_id" class="sub-value">{{ row.data.account_id }}</div>
						</template>
					</template>
				</p-column>
				<p-column
					header="Total"
					sortable
					:field="metric_view === 'spend' ? 'total_spend' : 'total_cpa'"
					frozen
					class="last-frozen column-align-right"
				>
					<template #body="row">
						<div class="frozen-border"></div>
						<template v-if="row.data.total">
							<strong>{{ row.data.currency ? currency(row.data.total_value) : row.data.total_value }}</strong>
						</template>
						<template v-else>
							<template v-if="metric_view === 'spend'">
								{{ currency(row.data.total_spend || 0) }} ({{ currency(row.data.total_cpl || 0) }})
							</template>
							<template v-else>
								{{ currency(row.data.average_cpa || 0) }} ({{ currency(row.data.average_cr || 0) }})
							</template>
						</template>
					</template>
				</p-column>
				<p-column
					v-for="(grouping, index) in reportDatesArray"
					:header="grouping"
					:key="grouping"
					sortable
					:field="`data.${grouping}.${metric_view === 'spend' ? 'spend' : 'cpa'}`"
					:class="['shaded', 'column-align-right', { weekend: isWeekend(grouping) }]"
				>
					<template #body="row">
						<template v-if="row.data.total">
							<div class="color-cell">
								<strong>{{
									row.data.currency ? currency(row.data.data[grouping] || 0) : row.data.data[grouping] || 0
								}}</strong>
							</div>
						</template>
						<template v-else>
							<div :class="cellClass(grouping, row)">
								{{
									currency(
										(metric_view === 'spend' ? row.data.data[grouping]?.spend : row.data.data[grouping]?.cpa) || 0
									)
								}}
								({{
									metric_view === 'spend'
										? currency(row.data.data[grouping]?.cpl)
										: percentage(row.data.data[grouping]?.cr) || 0
								}})<br />
								<div
									v-if="!row.data.total"
									class="sub-value"
									v-html="
										index > 0
											? trend(
													metric_view === 'spend' ? row.data.data[grouping]?.spend : row.data.data[grouping]?.cpa,
													metric_view === 'spend'
														? row.data.data[reportDatesArray[index - 1]]?.spend
														: row.data.data[reportDatesArray[index - 1]]?.cpa
												)
											: '--'
									"
								></div>
							</div>
						</template>
					</template>
				</p-column>
			</p-data-table>
		</div>
	</div>
</template>

<script>
import dateRangePicker from '@/components/forms/DateRangePicker.vue';
import radioGroup from '@/components/forms/RadioGroup.vue';
import queryForm from '@/components/widgets/QueryForm.vue';
import quickFilter from '@/components/widgets/QuickFilter.vue';
import { colorCell, currency, displayLabel, displaySelected, formatDate, percentage, trend } from '@/lib/Filters';
import { cloneDeep, isEmpty, mapValues, min, max } from 'lodash-es';
import { getAccountManagersAsOptions, reportAMBookTracker } from '@GQL';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isSameOrBefore);
dayjs.extend(isoWeek);

export default {
	name: 'AMBookTrackerTable',
	components: {
		dateRangePicker,
		radioGroup,
		queryForm,
		quickFilter,
	},
	data() {
		return {
			loading: false,
			query: {
				account_manager_id: null,
				grouping: 'day',
				date_range: [dayjs().startOf('year').toDate(), dayjs().subtract(1, 'day').endOf('day').toDate()],
				filters: {
					campaign_type: [],
					vertical_id: [],
				},
			},
			grouping: 'day',
			metric_view: 'spend',
			report_dates: [],
			report_data: [],
			raw_totals: null,
			account_manager_options: [],
			grouping_options: [
				{ label: 'Day', value: 'day' },
				{ label: 'Week', value: 'week' },
				{ label: 'Month', value: 'month' },
			],
			campaign_type_options: [
				{ label: 'Exclusive Data', value: 'exclusive_data' },
				{ label: 'Shared Data', value: 'shared_data' },
				{ label: 'Premium Listing', value: 'premium_listing' },
				{ label: 'Call', value: 'call' },
				{ label: 'Live Transfer', value: 'live_transfer' },
			],
			date_picker_options: [
				{
					label: 'This Week',
					icon: 'calendar-today',
					value: [dayjs().startOf('isoWeek').toDate(), dayjs().endOf('isoWeek').toDate()],
				},
				{
					label: 'This Month',
					icon: 'calendar-today',
					value: [dayjs().startOf('month').toDate(), dayjs().subtract(1, 'day').endOf('day').toDate()],
				},
				{
					label: 'Last Month',
					icon: 'calendar-today',
					value: [
						dayjs().subtract(1, 'month').startOf('month').toDate(),
						dayjs().subtract(1, 'month').endOf('month').toDate(),
					],
				},
				{
					label: 'Last 4 Weeks',
					icon: 'calendar-today',
					value: [dayjs().subtract(3, 'weeks').startOf('isoWeek').toDate(), dayjs().endOf('isoWeek').toDate()],
				},
				{
					icon: 'calendar-today',
					label: 'Last 6 Months',
					value: [
						dayjs().subtract(5, 'months').startOf('month').toDate(),
						dayjs().subtract(1, 'day').endOf('day').toDate(),
					],
				},
				{
					icon: 'calendar-today',
					label: 'Last 12 Months',
					value: [
						dayjs().subtract(11, 'months').startOf('month').toDate(),
						dayjs().subtract(1, 'day').endOf('day').toDate(),
					],
				},
				{
					icon: 'calendar-today',
					label: 'This Calendar Year',
					value: [dayjs().startOf('year').toDate(), dayjs().subtract(1, 'day').endOf('day').toDate()],
				},
				{
					icon: 'calendar-today',
					label: 'Last Calendar Year',
					value: [
						dayjs().startOf('year').subtract(1, 'year').toDate(),
						dayjs().subtract(1, 'year').endOf('year').toDate(),
					],
				},
			],
		};
	},
	computed: {
		reportDatesArray() {
			const dates = [];
			let period = dayjs(this.report_dates[0]);
			if (this.grouping === 'week') {
				period = period.startOf('isoWeek');
			}
			while (period.isSameOrBefore(this.report_dates[1])) {
				if (this.grouping === 'month') {
					dates.push(period.format('YYYY-MM'));
				} else {
					dates.push(period.format('YYYY-MM-DD'));
				}
				period = period.add(1, this.grouping);
			}
			return dates;
		},
		totalsRows() {
			const totals = [];
			if (this.raw_totals && this.report_data.length) {
				if (this.metric_view === 'spend') {
					totals.push(
						{
							account_name: 'Total Spend',
							currency: true,
							total: true,
							total_value: this.raw_totals.total_spend.total_spend,
							data: mapValues(this.raw_totals.total_spend.data, 'spend'),
						},
						{
							account_name: 'Spend / Account',
							currency: true,
							total: true,
							total_value:
								this.raw_totals.total_book_size.average_book_size > 0
									? this.raw_totals.total_spend.total_spend / this.raw_totals.total_book_size.average_book_size
									: 0,
							data: mapValues(this.raw_totals.total_book_size.data, (value, key) => {
								return value.book_size > 0 ? this.raw_totals.total_spend.data[key].spend / value.book_size : 0;
							}),
						}
					);
				} else {
					totals.push({
						account_name: 'Average CPA',
						currency: true,
						total: true,
						total_value: this.raw_totals.average_cpa.average_cpa,
						data: mapValues(this.raw_totals.average_cpa.data, 'cpa'),
					});
				}

				totals.push(
					{
						account_name: 'Book Size',
						currency: false,
						total: true,
						total_value: this.raw_totals.total_book_size.average_book_size || 0,
						data: mapValues(this.raw_totals.total_book_size.data, 'book_size'),
					},
					{
						account_name: 'Active Accounts',
						currency: false,
						total: true,
						total_value: this.raw_totals.total_active.average_active,
						data: mapValues(this.raw_totals.total_active.data, 'active'),
					},
					{
						account_name: 'Active %',
						currency: false,
						total: true,
						total_value:
							this.raw_totals.total_book_size.average_book_size > 0
								? percentage(
										this.raw_totals.total_active.average_active / this.raw_totals.total_book_size.average_book_size,
										2
									)
								: 'NA',
						data: mapValues(this.raw_totals.total_book_size.data, (value, key) => {
							return value ? percentage(this.raw_totals.total_active.data[key].active / value.book_size) : 'NA';
						}),
					}
				);
			}

			return totals;
		},
	},
	methods: {
		colorCell,
		currency,
		displayLabel,
		displaySelected,
		formatDate,
		percentage,
		trend,
		async getReportData(query, refresh) {
			this.report_dates = cloneDeep(query.date_range);
			this.grouping = query.grouping;

			if (query.account_manager_id) {
				try {
					this.loading = true;
					const { results, totals } = await reportAMBookTracker(query, refresh);
					this.report_data = results;
					this.raw_totals = totals;
				} finally {
					this.loading = false;
				}
			}

			// Scroll all the way right
			if (this.report_data.length) {
				this.$nextTick(() => {
					document.querySelector('.p-datatable-wrapper').scrollLeft =
						document.querySelector('.p-datatable-wrapper').scrollWidth;
				});
			}
		},
		isWeekend(value) {
			if (this.grouping !== 'day') {
				return false;
			}
			return dayjs(value).day() === 0 || dayjs(value).day() === 6;
		},
		maxAccountValue(row, metric) {
			if (isEmpty(row.data.data)) return 100;
			return max(Object.values(row.data.data).map((v) => v[metric]));
		},
		minAccountValue(row, metric) {
			if (isEmpty(row.data.data)) return 0;
			return min(Object.values(row.data.data).map((v) => v[metric]));
		},
		cellClass(grouping, row) {
			if (!this.isWeekend(grouping)) {
				if (this.metric_view === 'spend') {
					return (
						colorCell(
							row.data.data[grouping]?.spend || 0,
							this.minAccountValue(row, 'spend'),
							this.maxAccountValue(row, 'spend')
						) || 'color-cell'
					);
				} else {
					return (
						colorCell(
							row.data.data[grouping]?.cpa || 0,
							this.minAccountValue(row, 'cpa'),
							this.maxAccountValue(row, 'cpa'),
							true
						) || 'color-cell'
					);
				}
			}
			return 'color-cell';
		},
		clearQuery() {
			this.loading = false;
		},
	},
	async mounted() {
		const account_managers = await getAccountManagersAsOptions(['account_manager', 'management']);
		if (account_managers[0]) {
			this.account_manager_options = account_managers[0].items;
			this.account_manager_options.unshift({
				label: 'None Selected',
				value: null,
			});
		}

		// Check if the current user is an AM
		const selected_am = this.account_manager_options.find((option) => {
			return option.value === this.$root.sessionStore.user.id;
		});
		if (selected_am || this.$route.params.account_manager_id) {
			this.query.account_manager_id = this.$route.params.account_manager_id || this.$root.sessionStore.user.id;
			await this.getReportData(this.query, true);
		}
	},
};
</script>

<style lang="less" scoped>
:deep(.weekend) {
	background-color: rgba(0, 0, 0, 0.03);
	border-left: 1px solid var(--gray-20) !important;
	border-right: 1px solid var(--gray-20) !important;
}

.am-name {
	margin-bottom: 0;
	margin-right: 1em;
}

.frozen-border {
	background-color: var(--gray-10);
	bottom: 0;
	position: absolute;
	right: 0;
	top: 0;
	width: 2px;
}

:deep(td.shaded) {
	padding: 0 !important;
}

:deep(.color-cell) {
	padding: calc(0.75rem - 1px) 1rem;

	.sub-value {
		background-color: #fff;
		border-radius: 3px;
		display: inline-block;
		padding: 0 3px;
	}
}
</style>
